import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from "@/contexts";
import { useNotificationsBadge } from "@/contexts";
import { usePushOptIn } from "@/contexts/PushOptInContext";
import { getPlatform } from "@/lib/platform";
import {
  fetchNotifications,
  groupNotificationsByTime,
  markNotificationAsRead,
  markNotificationsAsReadBatch,
} from "@/api/notifications";
import type { NotificationActor } from "@/api/notifications";
import { followCreator } from "@/api/creators";
import CloseButton from "@/components/Buttons/CloseButton/CloseButton";
import "./Notifications.css";
import "@/components/PushNotifications/PushPermissionPrompt.css";
import Notification from "@/models/notification";
import { error as logError } from "@/utils/logger";
import { WebAnalytics } from "@/lib/analytics";
import {
  EVENT_NOTIFICATIONS_AUTO_MARK_ALL_READ,
  EVENT_NOTIFICATIONS_PANEL_OPENED,
} from "@/lib/analyticsEvents";
import { NOTIFICATIONS_AUTO_MARK_READ_DELAY_MS } from "@/constants/notificationsUx";

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  entryPoint?: 'header_mobile' | 'navigation_menu' | 'notifications_page' | 'other';
}

function isUnread(n: Notification): boolean {
  return n.status !== "read";
}

/** Format a date as relative time (e.g. "about 4 hours ago", "2 months ago") for display on notification items. Uses round-to-nearest for hours to match iOS. */
function formatRelativeTime(dateInput: Date | string): string {
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHoursRaw = diffMin / 60;
  const diffHours = diffHoursRaw >= 24 ? Math.floor(diffHoursRaw) : Math.round(diffHoursRaw);
  const diffDays = Math.floor(diffHoursRaw / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffSec < 60) return "Just now";
  if (diffMin < 60) return diffMin === 1 ? "1 minute ago" : `${diffMin} minutes ago`;
  if (diffHoursRaw < 24) {
    const h = diffHours < 1 ? 1 : diffHours;
    return h === 1 ? "about 1 hour ago" : `about ${h} hours ago`;
  }
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffWeeks === 1) return "1 week ago";
  if (diffWeeks < 4) return `${diffWeeks} weeks ago`;
  if (diffMonths === 1) return "1 month ago";
  if (diffMonths < 12) return `${diffMonths} months ago`;
  if (diffYears === 1) return "1 year ago";
  return `${diffYears} years ago`;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  isOpen,
  onClose,
  entryPoint = 'other',
}) => {
  const router = useNavigate();
  const { user } = useAuth();
  const { setUnreadCount } = useNotificationsBadge();
  const {
    pushNotificationsAllowed,
    pushBannerMessage,
    requestPushPermissionFromBanner,
    refreshPushPermissionStatus,
  } = usePushOptIn();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [followingInProgress, setFollowingInProgress] = useState<Set<string>>(new Set());
  const [followedUsers, setFollowedUsers] = useState<Set<string>>(new Set());
  const [seeWhoNotificationId, setSeeWhoNotificationId] = useState<string | null>(null);
  const notificationsRef = useRef<Notification[]>([]);
  const wasOpenRef = useRef(false);
  notificationsRef.current = notifications;

  useEffect(() => {
    if (isOpen && getPlatform() === 'ios') {
      void refreshPushPermissionStatus();
    }
  }, [isOpen, refreshPushPermissionStatus]);

  // Desktop/tablet only (see .nav-rail-active media query in LayoutWrapper.css): push
  // page content/header over instead of floating this panel on top of them, same as
  // the Explore/Create rail flyouts (NavFlyoutPanel.tsx).
  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("nav-flyout-open");
    return () => document.body.classList.remove("nav-flyout-open");
  }, [isOpen]);

  const unreadCount = notifications.filter((n) => isUnread(n)).length;

  useEffect(() => {
    setUnreadCount(unreadCount);
  }, [unreadCount, setUnreadCount]);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      WebAnalytics.track(EVENT_NOTIFICATIONS_PANEL_OPENED, {
        entry_point: entryPoint,
      });
    }
    wasOpenRef.current = isOpen;
  }, [entryPoint, isOpen]);

  useEffect(() => {
    if (isOpen && user?._id) {
      const loadNotifications = async () => {
        setLoading(true);
        setError(null);
        const prevReadIds = new Set(
          notificationsRef.current.filter((n) => !isUnread(n)).map((n) => n._id)
        );
        try {
          const data = await fetchNotifications(user._id);
          const mapped = data.map((item: unknown) => new Notification(item as Record<string, unknown>));
          mapped.forEach((n) => {
            if (prevReadIds.has(n._id)) {
              n.status = "read";
              if (!n.timestamps.read) {
                n.timestamps = { ...n.timestamps, read: new Date().toISOString() };
              }
            }
          });
          setNotifications(mapped);
        } catch (err: unknown) {
          logError("Failed to load notifications:", err);
          setError("Failed to load notifications");
        } finally {
          setLoading(false);
        }
      };
      loadNotifications();
    }
  }, [isOpen, user?._id]);

  const setNotificationsReadLocal = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    const readAt = new Date().toISOString();
    setNotifications((prev) => {
      prev.forEach((notification) => {
        if (idSet.has(notification._id)) {
          notification.status = "read";
          notification.timestamps = { ...notification.timestamps, read: readAt };
        }
      });
      return [...prev];
    });
  }, []);

  useEffect(() => {
    if (!isOpen || !user?._id) return;

    const unreadNotifications = notifications.filter((notification) => isUnread(notification));
    if (unreadNotifications.length === 0) return;

    const localIds = new Set<string>();
    const backendIds = new Set<string>();

    unreadNotifications.forEach((notification) => {
      localIds.add(notification._id);
      const originalNotificationIds = notification.content.data.originalNotificationIds ?? [];
      if (originalNotificationIds.length > 0) {
        originalNotificationIds.forEach((id) => {
          if (!id.startsWith('group_')) {
            backendIds.add(id);
          }
        });
      } else if (!notification._id.startsWith('group_')) {
        backendIds.add(notification._id);
      }
    });

    const timerId = window.setTimeout(async () => {
      try {
        if (backendIds.size > 0) {
          await markNotificationsAsReadBatch(Array.from(backendIds), user._id);
        }
      } catch (err) {
        logError('Failed to auto-mark notifications as read:', err);
      } finally {
        setNotificationsReadLocal(Array.from(localIds));
        WebAnalytics.track(EVENT_NOTIFICATIONS_AUTO_MARK_ALL_READ, {
          entry_point: entryPoint,
          unread_count_at_open: unreadNotifications.length,
          backend_marked_count: backendIds.size,
          delay_ms: NOTIFICATIONS_AUTO_MARK_READ_DELAY_MS,
        });
      }
    }, NOTIFICATIONS_AUTO_MARK_READ_DELAY_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [entryPoint, isOpen, notifications, setNotificationsReadLocal, user?._id]);

  const getActionText = (notification: Notification): string => {
    const { data } = notification.content;
    if (data.isCollapsed && data.collapsedCount && data.collapsedCount > 1) {
      return notification.content.body ?? "";
    }
    switch (notification.type) {
      case "follow":
        return "started following you";
      case "post_like":
        return "liked your post";
      case "post_save":
        return "saved your post";
      case "post_comment":
        return "commented on your post";
      case "post_remix":
        return "remixed your post";
      default:
        return notification.content.body ?? "";
    }
  };

  /** First line for notification (name / title). For grouped follow, use backend title; else name. */
  const getNotificationLine1 = (notification: Notification): { text: string; boldName?: string } => {
    const { data } = notification.content;
    const isGroupedFollow =
      notification.type === "follow" &&
      ((data.actorCount ?? 0) > 1 || (data.actors?.length ?? 0) > 1 || (data.actorIds?.length ?? 0) > 1);
    if (isGroupedFollow && notification.content.title) {
      return { text: notification.content.title };
    }
    if (isGroupedFollow) {
      const name = getUserName(notification);
      const count = data.actorCount ?? data.actors?.length ?? data.actorIds?.length ?? 2;
      const others = count - 1;
      return { text: others > 0 ? `${name} and ${others} other${others === 1 ? "" : "s"}` : name };
    }
    return { text: getUserName(notification), boldName: getUserName(notification) };
  };

  /** Second line (e.g. "You have 2 new followers" or post title). Only for grouped follow or post with title. */
  const getNotificationLine2 = (notification: Notification): string | null => {
    const { data } = notification.content;
    const isGroupedFollow =
      notification.type === "follow" &&
      ((data.actorCount ?? 0) > 1 || (data.actors?.length ?? 0) > 1 || (data.actorIds?.length ?? 0) > 1);
    if (isGroupedFollow && notification.content.body) return notification.content.body;
    const postName = data.postTitle || data.postName;
    if (postName) return postName;
    return null;
  };

  const getUserName = (notification: Notification): string => {
    const { data } = notification.content;
    return data.followerUsername || data.actorUsername || "Someone";
  };

  const getActorsForAvatar = (notification: Notification): NotificationActor[] => {
    const { data } = notification.content;
    if (data.actors && data.actors.length > 0) return data.actors;
    if (data.actorId)
      return [{ id: data.actorId, name: data.actorUsername, avatarUrl: data.actorAvatarUrl ?? undefined }];
    if (data.followerId)
      return [{ id: data.followerId, name: data.followerUsername, avatarUrl: data.actorAvatarUrl ?? undefined }];
    return [{ id: "", name: getUserName(notification), avatarUrl: undefined }];
  };

  const getUserAvatarUrl = (notification: Notification): string => {
    const { data } = notification.content;
    if (data.actorAvatarUrl) return data.actorAvatarUrl;
    const actors = getActorsForAvatar(notification);
    const first = actors[0];
    if (first?.avatarUrl) return first.avatarUrl;
    const name = first?.name || getUserName(notification);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&size=100&background=random`;
  };

  const showFollowButton = (notification: Notification): boolean => {
    if (notification.type !== "follow" || !isUnread(notification)) return false;
    const { actorCount, actors, actorIds, followerId } = notification.content.data;
    const isGroupedFollow = (actorCount ?? 0) > 1 || (actors?.length ?? 0) > 1 || (actorIds?.length ?? 0) > 1;
    if (isGroupedFollow) return false;
    if (!followerId || followedUsers.has(followerId)) return false;
    return true;
  };

  const handleFollowBack = async (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation();
    const followerId = notification.content.data.followerId;
    if (!followerId) return;
    setFollowingInProgress((prev) => new Set(prev).add(followerId));
    try {
      await followCreator(followerId);
      setFollowedUsers((prev) => new Set(prev).add(followerId));
    } catch (err: unknown) {
      logError("Failed to follow user:", err);
      alert("Failed to follow user. Please try again.");
    } finally {
      setFollowingInProgress((prev) => {
        const next = new Set(prev);
        next.delete(followerId);
        return next;
      });
    }
  };

  const handleFollowBackById = async (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    if (followedUsers.has(userId)) return;
    setFollowingInProgress((prev) => new Set(prev).add(userId));
    try {
      await followCreator(userId);
      setFollowedUsers((prev) => new Set(prev).add(userId));
    } catch (err: unknown) {
      logError("Failed to follow user:", err);
      alert("Failed to follow user. Please try again.");
    } finally {
      setFollowingInProgress((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const handleNotificationTap = async (notification: Notification) => {
    if (!user?._id) return;
    const idsToMark = notification.content.data.originalNotificationIds?.length
      ? notification.content.data.originalNotificationIds
      : [notification._id];
    if (idsToMark.some((id: string) => id.startsWith("group_"))) {
      const realIds = notification.content.data.originalNotificationIds || [];
      if (realIds.length === 0) {
        setNotificationsReadLocal([notification._id]);
      } else {
        try {
          await markNotificationsAsReadBatch(realIds, user._id);
          setNotificationsReadLocal([notification._id, ...realIds]);
        } catch {
          setNotificationsReadLocal([notification._id]);
        }
      }
    } else {
      try {
        await markNotificationAsRead(notification._id, user._id);
        setNotificationsReadLocal([notification._id]);
      } catch {
        setNotificationsReadLocal([notification._id]);
      }
    }

    const { data } = notification.content;
    if (data.postId) {
      onClose();
      router(`/post/${data.postId}`);
      return;
    }
    const profileId = data.actorId || data.followerId;
    if (profileId) {
      onClose();
      router(`/profile/${profileId}`);
    }
  };

  const grouped = groupNotificationsByTime(notifications);

  const isGrouped = (n: Notification) =>
    (n.content.data.isCollapsed && (n.content.data.actorCount ?? 0) > 1) ||
    (n.content.data.originalNotificationIds?.length ?? 0) > 1;

  const renderNotificationRow = (notification: Notification) => {
    const actors = getActorsForAvatar(notification);
    const groupedDisplay = isGrouped(notification);
    const primaryAvatarUrl = getUserAvatarUrl(notification);

    return (
      <div
        key={notification._id}
        className={`notification-item ${!isUnread(notification) ? "notification-item-read" : ""}`}
        onClick={() => handleNotificationTap(notification)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleNotificationTap(notification);
          }
        }}
        aria-label={`Notification: ${getUserName(notification)} ${getActionText(notification)}`}
      >
        <div className="notification-avatar-wrap">
          {groupedDisplay && actors.length > 1 ? (
            <div className="notification-avatars-stacked">
              {actors.slice(0, 3).map((a, i) => (
                <img
                  key={a.id || i}
                  src={a.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(a.name || "?")}&size=80&background=random`}
                  alt=""
                  className="notification-avatar-stack"
                  style={{ zIndex: 3 - i }}
                />
              ))}
            </div>
          ) : (
            <img
              src={primaryAvatarUrl}
              alt={getUserName(notification)}
              className="notification-avatar"
            />
          )}
        </div>
        <div className="notification-body">
          {(() => {
            const line1 = getNotificationLine1(notification);
            const line2 = getNotificationLine2(notification);
            return (
              <>
                <p className="notification-text">
                  {line1.boldName ? (
                    <>
                      <strong>{line1.boldName}</strong> {getActionText(notification)}
                    </>
                  ) : (
                    line1.text
                  )}
                </p>
                {line2 && <p className="notification-subline">{line2}</p>}
                <p className="notification-time" aria-label={`${formatRelativeTime(notification.timestamps.created)}`}>
                  {formatRelativeTime(notification.timestamps.created)}
                </p>
              </>
            );
          })()}
          {groupedDisplay && (notification.content.data.actorCount ?? 0) > 1 && (
            <button
              type="button"
              className="notification-see-who"
              onClick={(e) => {
                e.stopPropagation();
                setSeeWhoNotificationId(seeWhoNotificationId === notification._id ? null : notification._id);
              }}
            >
              See who{" "}
              {notification.type === "follow"
                ? "followed"
                : notification.type === "post_save"
                  ? "saved"
                  : notification.type === "post_like"
                    ? "liked"
                    : notification.type === "post_remix"
                      ? "remixed"
                      : "commented"}
            </button>
          )}
        </div>
        {showFollowButton(notification) && (
          <button
            type="button"
            className="notification-action-btn"
            onClick={(e) => handleFollowBack(e, notification)}
            disabled={followingInProgress.has(notification.content.data.followerId!)}
          >
            {followingInProgress.has(notification.content.data.followerId!) ? "Following..." : "Follow Back"}
          </button>
        )}
      </div>
    );
  };

  const seeWhoNotification = seeWhoNotificationId
    ? notifications.find((n) => n._id === seeWhoNotificationId)
    : null;

  return (
    <>
      {isOpen && <div className="notifications-overlay" onClick={onClose} aria-hidden />}
      <div className={`notifications-panel ${isOpen ? "open" : ""}`}>
        <div className="notifications-header">
          <h2 className="notifications-title">
            Notifications
            <span className="notifications-title-count">{unreadCount}</span>
          </h2>
          <CloseButton onClick={onClose} ariaLabel="Close" size={20} />
        </div>

        <div className="notifications-content">
          {getPlatform() === 'ios' && !pushNotificationsAllowed && pushBannerMessage ? (
            <button
              type="button"
              className="notifications-push-banner"
              onClick={() => void requestPushPermissionFromBanner()}
            >
              {pushBannerMessage}
            </button>
          ) : null}
          {loading && (
            <div className="notifications-loading">Loading notifications...</div>
          )}
          {error && <div className="notifications-error">{error}</div>}
          {!loading && !error && notifications.length === 0 && (
            <div className="notifications-empty">No notifications yet</div>
          )}

          {!loading && !error && grouped.today.length > 0 && (
            <div className="notifications-section">
              <h3 className="notifications-section-title">Today</h3>
              {grouped.today.map(renderNotificationRow)}
            </div>
          )}
          {!loading && !error && grouped.yesterday.length > 0 && (
            <div className="notifications-section">
              <h3 className="notifications-section-title">Yesterday</h3>
              {grouped.yesterday.map(renderNotificationRow)}
            </div>
          )}
          {!loading && !error && grouped.last7Days.length > 0 && (
            <div className="notifications-section">
              <h3 className="notifications-section-title">Earlier</h3>
              {grouped.last7Days.map(renderNotificationRow)}
            </div>
          )}
        </div>
      </div>

      {seeWhoNotification && (
        <>
          <div
            className="notifications-overlay"
            onClick={() => setSeeWhoNotificationId(null)}
            aria-hidden
          />
          <div className="notifications-see-who-modal" role="dialog" aria-label="Who reacted">
          <div className="notifications-see-who-header">
            <h3>See who</h3>
            <button
              type="button"
              className="notifications-see-who-close"
              onClick={() => setSeeWhoNotificationId(null)}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <ul className="notifications-see-who-list">
            {getActorsForAvatar(seeWhoNotification).map((actor) => (
              <li key={actor.id} className="notifications-see-who-list-item">
                <img
                  src={actor.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(actor.name || "?")}&size=80&background=random`}
                  alt=""
                  className="notification-avatar"
                />
                <span className="notifications-see-who-name">{actor.name || "Someone"}</span>
                {seeWhoNotification.type === "follow" && actor.id && (
                  <button
                    type="button"
                    className="notification-action-btn notifications-see-who-follow-btn"
                    onClick={(e) => handleFollowBackById(e, actor.id)}
                    disabled={followedUsers.has(actor.id) || followingInProgress.has(actor.id)}
                  >
                    {followedUsers.has(actor.id) ? "Following" : followingInProgress.has(actor.id) ? "Following..." : "Follow Back"}
                  </button>
                )}
              </li>
            ))}
          </ul>
          </div>
        </>
      )}
    </>
  );
};

export default NotificationsPanel;
